import { useState } from "react";
import { api } from "../api";
import { useAsync } from "../hooks";
import { fmt } from "../format";
import { useConfirmAction } from "./useConfirmAction";

/** Gallery row (mirrors the api TemplateSummary). */
type TemplateSummary = {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  site_host: string | null;
  fetch_mode: string;
  field_count: number;
  version: number;
  /** True for the caller's OWN personal template; false for the shared operator catalog. */
  personal: boolean;
  created_at: string;
};

/** Full recipe incl. its config bundle (GET /templates/:id) — used to prefill the instantiate form. */
type TemplateDetail = TemplateSummary & {
  bundle: { name: string; target_urls: string[]; fetch_mode: string };
};

type Scraper = { id: string; name: string };

/**
 * Templates — the shared gallery of ready-made scraper setups. Instead of building (or AI-generating) a
 * scraper from scratch for a site already covered, pick a template and instantiate it into your
 * workspace as your own scraper, optionally pointed at your own URLs. Consumption side only; templates
 * are published by the operator from a scraper's Settings → "Publish as template". When `embedded` it
 * renders inside the Scrapers page (the section switch supplies the heading), so its own is suppressed.
 */
export function Recipes({
  onNavToFleet,
  embedded = false,
}: {
  onNavToFleet: () => void;
  embedded?: boolean;
}) {
  const { data, loading, error, reload } = useAsync<TemplateSummary[]>(() => api("/templates"), []);
  const [use, setUse] = useState<TemplateSummary | null>(null);
  const [created, setCreated] = useState<string | null>(null);
  const confirmRemove = useConfirmAction();
  const recipes = data ?? [];

  function remove(t: TemplateSummary) {
    confirmRemove.ask({
      title: `Remove personal template "${t.name}"?`,
      body: "It's removed from your Templates. Scrapers you already created from it keep working.",
      confirmLabel: "Remove",
      danger: true,
      run: async () => {
        await api(`/templates/${t.id}`, { method: "DELETE" });
        reload();
      },
    });
  }

  return (
    <div className="stack">
      {!embedded && (
        <div className="section-head">
          <h1>Templates</h1>
        </div>
      )}
      <p className="muted" style={{ marginTop: 0, maxWidth: 640 }}>
        Ready-made scraper setups for common sites. Pick one and it becomes your own scraper — scheduled,
        self-healing, and pointed at the URLs you choose. No need to rebuild an extractor for a site
        that's already covered.
      </p>

      {created && (
        <div
          className="mt"
          style={{
            border: "1px solid var(--rule)",
            borderRadius: "var(--r)",
            padding: "var(--sp3)",
            background: "var(--wash)",
          }}
        >
          Created <b>{created}</b> in your scrapers.{" "}
          <button className="link-btn" onClick={onNavToFleet}>
            Open it →
          </button>
        </div>
      )}

      {loading && !data && <div className="muted pad">Loading…</div>}
      {error && <div className="err pad">Error: {error}</div>}
      {!loading && recipes.length === 0 && (
        <div className="empty onboard">
          <h3>No templates yet</h3>
          <p>Templates published from a scraper's Settings tab show up here, ready to reuse.</p>
        </div>
      )}

      {recipes.length > 0 && (
        <div
          style={{
            display: "grid",
            gap: "var(--sp3)",
            gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
          }}
        >
          {recipes.map((r) => (
            <div
              key={r.id}
              style={{
                border: "1px solid var(--rule)",
                borderRadius: "var(--r)",
                padding: "var(--sp3)",
                display: "flex",
                flexDirection: "column",
                gap: 6,
              }}
            >
              <div className="row-gap" style={{ justifyContent: "space-between" }}>
                <b>{r.name}</b>
                <span className="row-gap">
                  {r.personal && <span className="pill">yours</span>}
                  {r.site_host && <span className="pill hold">{r.site_host}</span>}
                </span>
              </div>
              {r.description && <div className="muted sm">{r.description}</div>}
              <div className="muted sm">
                {r.field_count} field{r.field_count === 1 ? "" : "s"} · {r.fetch_mode} · v{r.version} ·
                added {fmt(r.created_at)}
              </div>
              <div style={{ flex: 1 }} />
              <div className="row-gap mt">
                <button className="btn sm" onClick={() => setUse(r)}>
                  Use template
                </button>
                {r.personal && (
                  <button className="btn ghost sm danger" onClick={() => remove(r)}>
                    Remove
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {use && (
        <UseRecipeModal
          recipe={use}
          onClose={() => setUse(null)}
          onCreated={(s) => {
            setCreated(s.name);
            setUse(null);
          }}
        />
      )}
      {confirmRemove.modal}
    </div>
  );
}

/**
 * Instantiate a recipe: name the new scraper and (optionally) point it at your own URLs, then create it.
 * Prefills from the recipe's own defaults (fetched via GET /templates/:id). POSTs the instantiate route.
 */
function UseRecipeModal({
  recipe,
  onClose,
  onCreated,
}: {
  recipe: TemplateSummary;
  onClose: () => void;
  onCreated: (scraper: Scraper) => void;
}) {
  const { data: detail } = useAsync<TemplateDetail>(() => api(`/templates/${recipe.id}`), [recipe.id]);
  const [name, setName] = useState(recipe.name);
  const [urls, setUrls] = useState("");
  const [touchedUrls, setTouchedUrls] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Prefill the URL box with the recipe's own targets once the detail loads (unless the user typed).
  const defaults = detail?.bundle.target_urls ?? [];
  const urlValue = touchedUrls ? urls : defaults.join("\n");

  async function create() {
    setBusy(true);
    setErr(null);
    try {
      const list = urlValue
        .split("\n")
        .map((u) => u.trim())
        .filter(Boolean);
      const body: { name?: string; target_urls?: string[] } = {};
      if (name.trim() && name.trim() !== recipe.name) body.name = name.trim();
      // Only override targets when they differ from the recipe's own defaults.
      if (list.length > 0 && list.join("\n") !== defaults.join("\n")) body.target_urls = list;
      const scraper = await api<Scraper>(`/templates/${recipe.id}/instantiate`, {
        method: "POST",
        body: JSON.stringify(body),
      });
      onCreated(scraper);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "create failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="section-head">
          <h2>Use template · {recipe.name}</h2>
          <button className="btn ghost sm" onClick={onClose}>
            Close
          </button>
        </div>
        <p className="muted" style={{ marginTop: 0 }}>
          Creates a new scraper in your workspace from this template. Change the URLs to scrape your own
          pages of the same kind (one per line).
        </p>
        <div className="form-grid">
          <label htmlFor="use-name">Scraper name</label>
          <input id="use-name" value={name} onChange={(e) => setName(e.target.value)} />
          <label htmlFor="use-urls">URLs to scrape</label>
          <textarea
            id="use-urls"
            rows={4}
            placeholder={"https://…\nhttps://…"}
            value={urlValue}
            onChange={(e) => {
              setTouchedUrls(true);
              setUrls(e.target.value);
            }}
          />
        </div>
        {err && <div className="err mt">{err}</div>}
        <div className="row-gap mt" style={{ justifyContent: "flex-end", marginTop: "var(--sp5)" }}>
          <button className="btn ghost sm" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="btn" onClick={() => void create()} disabled={busy || !name.trim()}>
            {busy ? "Creating…" : "Create scraper"}
          </button>
        </div>
      </div>
    </div>
  );
}
